import React, { useEffect, useRef, useState } from 'react';
import './VConsole.css';

interface VConsoleInstance {
  destroy: () => void;
}

declare global {
  interface Window {
    VConsole: new (options?: any) => VConsoleInstance;
  }
}

const VConsole: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const vConsoleRef = useRef<VConsoleInstance | null>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Добавление кнопки закрытия в VConsole
  const addCloseButton = () => {
    // Удаляем существующую кнопку закрытия, если она есть
    const existingCloseBtn = document.querySelector('.vc-close-btn');
    if (existingCloseBtn) {
      existingCloseBtn.remove();
    }

    // Создаем кнопку закрытия
    const closeBtn = document.createElement('div');
    closeBtn.className = 'vc-close-btn';
    closeBtn.innerHTML = '✕';
    closeBtn.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      width: 40px;
      height: 40px;
      background: #dc3545;
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      cursor: pointer;
      z-index: 10003;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      transition: all 0.3s ease;
    `;

    // Добавляем hover эффект
    closeBtn.addEventListener('mouseenter', () => {
      closeBtn.style.transform = 'scale(1.1)';
      closeBtn.style.background = '#c82333';
    });

    closeBtn.addEventListener('mouseleave', () => {
      closeBtn.style.transform = 'scale(1)';
      closeBtn.style.background = '#dc3545';
    });

    // Обработчик клика
    closeBtn.addEventListener('click', () => {
      closeVConsole();
    });

    // Добавляем кнопку в body
    document.body.appendChild(closeBtn);
  };

  // Загрузка VConsole
  const loadVConsole = async (): Promise<VConsoleInstance> => {
    if (vConsoleRef.current) {
      return vConsoleRef.current;
    }

    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/vconsole@latest/dist/vconsole.min.js';
      //theme: 'dark',
             script.onload = () => {
         vConsoleRef.current = new window.VConsole({
           theme: 'dark',
           maxLogNumber: 1000,
           onReady: () => {
             console.log('VConsole is ready');
             
             // Скрываем VConsole по умолчанию
             const vConsoleContainer = document.querySelector('#__vconsole');
             if (vConsoleContainer) {
               (vConsoleContainer as HTMLElement).style.display = 'none';
               (vConsoleContainer as HTMLElement).style.visibility = 'hidden';
               (vConsoleContainer as HTMLElement).style.opacity = '0';
               console.log('VConsole hidden by default');
             }
           }
         });
         resolve(vConsoleRef.current);
       };
      document.body.appendChild(script);
    });
  };

  // Открытие VConsole на весь экран
  const openVConsole = async () => {
    await loadVConsole();
    
    setTimeout(() => {
      const vConsoleEl = document.querySelector('.vc-panel');
      const vConsoleContainer = document.querySelector('#__vconsole');
      
             if (vConsoleEl && vConsoleContainer) {
         // Показываем контейнер VConsole
         (vConsoleContainer as HTMLElement).style.display = 'block';
         (vConsoleContainer as HTMLElement).style.visibility = 'visible';
         (vConsoleContainer as HTMLElement).style.opacity = '1';
         
         // Добавляем классы для полноэкранного режима и фона
         vConsoleEl.classList.add('vc-fullscreen');
         vConsoleContainer.classList.add('vc-open');
         
         // Добавляем кнопку закрытия в VConsole
         addCloseButton();
         
         setIsOpen(true);
         console.log('VConsole opened in fullscreen mode');
       }
    }, 100);
  };

  // Сворачивание VConsole
  const closeVConsole = () => {
    const vConsoleEl = document.querySelector('.vc-panel');
    const vConsoleContainer = document.querySelector('#__vconsole');
    
    if (vConsoleEl && vConsoleContainer) {
      // Убираем классы для полноэкранного режима и фона
      vConsoleEl.classList.remove('vc-fullscreen');
      vConsoleContainer.classList.remove('vc-open');
      
      // Скрываем контейнер VConsole, но не очищаем полностью стили
      (vConsoleContainer as HTMLElement).style.display = 'none';
      (vConsoleContainer as HTMLElement).style.visibility = 'hidden';
      (vConsoleContainer as HTMLElement).style.opacity = '0';
      (vConsoleContainer as HTMLElement).style.background = 'transparent';
      
      // Удаляем кнопку закрытия
      const closeBtn = document.querySelector('.vc-close-btn');
      if (closeBtn) {
        closeBtn.remove();
      }
      
      setIsOpen(false);
      console.log('VConsole minimized');
    }
  };

  // Обработчик клика по кнопке
  const handleButtonClick = () => {
    if (!isOpen) {
      openVConsole();
    } else {
      closeVConsole();
    }
  };

  // Обработчик клавиши Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        buttonRef.current?.click();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Очистка при размонтировании компонента
  useEffect(() => {
    return () => {
      // Скрываем VConsole при размонтировании компонента
      const vConsoleContainer = document.querySelector('#__vconsole');
      if (vConsoleContainer) {
        // Убираем класс фона
        vConsoleContainer.classList.remove('vc-open');
        
        // Скрываем контейнер VConsole
        (vConsoleContainer as HTMLElement).style.display = 'none';
        (vConsoleContainer as HTMLElement).style.visibility = 'hidden';
        (vConsoleContainer as HTMLElement).style.opacity = '0';
        (vConsoleContainer as HTMLElement).style.background = 'transparent';
      }
      
      // Удаляем кнопку закрытия
      const closeBtn = document.querySelector('.vc-close-btn');
      if (closeBtn) {
        closeBtn.remove();
      }
    };
  }, []);

  return (
    <>
      <div
        ref={buttonRef}
        onClick={handleButtonClick}
                 style={{
           position: 'fixed',
           top: '20px',
           right: '20px',
           width: '50px',
           height: '50px',
           background: isOpen ? '#dc3545' : '#007bff',
           color: 'white',
           borderRadius: '50%',
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           fontSize: '20px',
           cursor: 'pointer',
           zIndex: 10000,
           boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
           transition: 'all 0.3s ease',
           opacity: 1,
           transform: isOpen ? 'scale(0.8)' : 'scale(1)',
         }}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.background = '#0056b3';
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.background = '#007bff';
          }
        }}
      >
        {isOpen ? '✕' : '🔧'}
             </div>
     </>
   );
 };

export default VConsole;
